import React, { useEffect, useRef, useState } from 'react';

// Small emoji picker for the internal comment composers.
//
// Deliberately no library: the app ships a strict CSP, every emoji-picker
// package of any size pulls a sprite sheet or a JSON catalogue from somewhere,
// and this needs about forty characters, not six thousand. The set below is
// what people actually reach for in work notes — reactions, status, and the
// handful of objects that come up on event briefs — grouped so it can be
// scanned rather than searched.
//
// Emoji are plain text, so nothing downstream needs to change: they store,
// render, search and email as-is.

const GROUPS = [
  {
    name: 'Reactions',
    emoji: ['👍', '👎', '👏', '🙌', '🙏', '💪', '🤝', '👀', '🎉', '🔥', '⭐', '❤️', '😀', '😅', '😂', '🙂', '😉', '😍', '🤔', '😬', '😱', '😴', '🤷', '🫡'],
  },
  {
    name: 'Status',
    emoji: ['✅', '❌', '⚠️', '🚩', '⏰', '📅', '⏳', '🔒', '🔑', '📌', '📎', '🔍', '💡', '❓', '❗', '🆕', '🔄', '➡️', '⬆️', '⬇️'],
  },
  {
    name: 'Work',
    emoji: ['📝', '📄', '📊', '📈', '📉', '💷', '💰', '🧾', '📦', '🚚', '🏗️', '🔧', '🎪', '🎤', '🎟️', '🍽️', '☕', '🚧', '📍', '🌧️'],
  },
];

export default function EmojiPicker({ onSelect, disabled = false, label = 'Insert emoji' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const firstRef = useRef(null);

  // Close on outside click or Escape. Escape also returns focus to the trigger
  // so keyboard users aren't dropped at the top of the document.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); wrapRef.current?.querySelector('button')?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  useEffect(() => { if (open) firstRef.current?.focus(); }, [open]);

  return (
    <span ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={label}
        aria-label={label}
        className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-line bg-white text-[14px] leading-none hover:border-[#BFC7AE] hover:bg-[#F7F9F0] transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-fusion-forest">
        <span aria-hidden="true">🙂</span>
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Emoji"
          // Opens UPWARD: the composer sits low on a long page, and a downward
          // panel would hang off the bottom.
          className="absolute bottom-full left-0 mb-2 z-30 w-[268px] bg-white border border-line rounded-xl shadow-lift p-2.5">
          {GROUPS.map((group, gi) => (
            <div key={group.name} className={gi > 0 ? 'mt-2.5' : ''}>
              <p className="font-['Metropolis'] text-[10px] font-semibold text-[#51534A] mb-1.5">{group.name}</p>
              <div className="grid grid-cols-8 gap-0.5">
                {group.emoji.map((e, i) => (
                  <button
                    key={e}
                    ref={gi === 0 && i === 0 ? firstRef : undefined}
                    type="button"
                    onClick={() => { onSelect?.(e); setOpen(false); }}
                    title={e}
                    className="w-7 h-7 inline-flex items-center justify-center rounded text-[16px] leading-none hover:bg-[#F0F3E6] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-fusion-forest">
                    <span aria-hidden="true">{e}</span>
                    <span className="sr-only">{e}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}
